import React from 'react';
import { InstructorLayout } from '../../components/instructor/InstructorLayout';
import { useInstructorPermissions } from '../../hooks/useInstructorPermissions';
import { useInstructorAuthContext } from '../../context/InstructorAuthContext';
import { MessageSquare, Send, Users, Plus, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';

const InstructorMessagesPage: React.FC = () => {
  const { instructor, hasPermission, isLoading, permissions } = useInstructorPermissions();
  const { refreshProfile } = useInstructorAuthContext();

  // Debug: Afficher les informations de l'instructeur et permissions
  console.log('InstructorMessagesPage - Debug Info:', {
    instructor,
    permissions,
    hasPermission: hasPermission('send_messages'),
    isLoading
  });

  // Fonction pour forcer le rafraîchissement des permissions
  const handleRefreshPermissions = async () => {
    console.log('Rafraîchissement des permissions...');
    await refreshProfile();
  };

  if (isLoading) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </InstructorLayout>
    );
  }

  // Vérification manuelle de la permission send_messages
  if (!hasPermission('send_messages')) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès restreint</h2>
            <p className="text-gray-600 mb-4">Vous n'avez pas la permission d'accéder aux messages.</p>
            <Button onClick={handleRefreshPermissions} variant="outline">
              Actualiser les permissions
            </Button>
          </div>
        </div>
      </InstructorLayout>
    );
  }

  return (
    <InstructorLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-indigo-400 to-fuchsia-400 drop-shadow-[0_1px_12px_rgba(99,102,241,0.25)]">Messages</h1>
            <p className="mt-1 text-sm md:text-base text-gray-500 dark:text-gray-400">
              Communiquez avec vos étudiants
            </p>
            {/* Debug info temporaire */}
            <div className="mt-3 px-3 py-2 rounded-md border border-amber-400/20 bg-amber-500/5 text-[10px] font-mono text-amber-300/90 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.12)] hidden md:block">
              <strong>Debug:</strong> Permissions = {JSON.stringify(permissions)} | 
              Has send_messages = {hasPermission('send_messages').toString()}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefreshPermissions} variant="outline" className="flex items-center gap-2 rounded-xl border-slate-800/60 bg-slate-900/30 hover:bg-slate-900/60 hover:border-indigo-500/40 transition-colors">
              Actualiser permissions
            </Button>
            <Button className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-500 hover:to-fuchsia-500 text-white shadow-[0_10px_30px_-10px_rgba(99,102,241,0.5)] border-0">
              <Plus className="h-4 w-4" />
              Nouveau message
            </Button>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Liste des conversations */}
          <div className="lg:col-span-1">
            <Card className="bg-slate-900/50 backdrop-blur supports-[backdrop-filter]:bg-slate-900/40 border border-slate-800/60 shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_10px_35px_-15px_rgba(0,0,0,0.6)] rounded-2xl overflow-hidden">
              <CardHeader className="border-b border-slate-800/60 pb-4">
                <CardTitle className="flex items-center gap-2 text-slate-200">
                  <MessageSquare className="h-5 w-5" />
                  Conversations
                </CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input placeholder="Rechercher..." className="pl-9 bg-slate-950/40 border-slate-800 text-slate-200 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:border-indigo-500 rounded-xl" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="p-3.5 border border-slate-800/60 rounded-xl bg-slate-900/30 hover:bg-slate-900/60 transition-all hover:shadow-[0_0_20px_rgba(99,102,241,0.15)] cursor-pointer">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-sm text-slate-200">Marie Kamga</h4>
                      <Badge variant="destructive" className="text-xs bg-rose-600/90 text-white shadow-[0_0_12px_rgba(244,63,94,0.35)]">2</Badge>
                    </div>
                    <p className="text-xs text-slate-400 truncate">
                      Question sur le chapitre 3...
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Il y a 2h</p>
                  </div>
                  
                  <div className="p-3.5 border border-slate-800/60 rounded-xl bg-slate-900/30 hover:bg-slate-900/60 transition-all hover:shadow-[0_0_20px_rgba(99,102,241,0.15)] cursor-pointer">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-sm text-slate-200">Paul Nguema</h4>
                    </div>
                    <p className="text-xs text-slate-400 truncate">
                      Merci pour l'explication...
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Hier</p>
                  </div>

                  <div className="p-3.5 border border-slate-800/60 rounded-xl bg-slate-900/30 hover:bg-slate-900/60 transition-all hover:shadow-[0_0_20px_rgba(99,102,241,0.15)] cursor-pointer">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-sm text-slate-200">Sarah Mballa</h4>
                    </div>
                    <p className="text-xs text-slate-400 truncate">
                      Problème avec l'exercice...
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Il y a 2 jours</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Zone de conversation */}
          <div className="lg:col-span-3">
            <Card className="h-96 flex flex-col bg-slate-900/50 backdrop-blur supports-[backdrop-filter]:bg-slate-900/40 border border-slate-800/60 rounded-2xl shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_10px_35px_-15px_rgba(0,0,0,0.6)]">
              <CardHeader className="border-b border-slate-800/60">
                <CardTitle className="text-slate-200">Messagerie</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col p-0">
                <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-slate-950/0 via-slate-950/20 to-slate-950/0">
                  <div className="text-center px-6">
                    <MessageSquare className="h-16 w-16 text-indigo-400/30 mx-auto mb-4 drop-shadow-[0_0_20px_rgba(99,102,241,0.35)]" />
                    <h3 className="text-lg font-semibold text-slate-200 mb-2">
                      Messagerie en cours de développement
                    </h3>
                    <p className="text-slate-400 mb-4">
                      Cette fonctionnalité sera bientôt disponible pour échanger avec vos étudiants.
                    </p>
                    <div className="space-y-2">
                      <Button variant="outline" className="w-full rounded-xl border-slate-800/60 bg-slate-900/30 hover:bg-slate-900/60 transition-colors">
                        <Users className="h-4 w-4 mr-2" />
                        Message groupé
                      </Button>
                      <Button variant="outline" className="w-full rounded-xl border-slate-800/60 bg-slate-900/30 hover:bg-slate-900/60 transition-colors">
                        <Send className="h-4 w-4 mr-2" />
                        Annonce générale
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-slate-900/50 backdrop-blur supports-[backdrop-filter]:bg-slate-900/40 border border-slate-800/60 rounded-2xl shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_10px_35px_-15px_rgba(0,0,0,0.6)]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 border-b border-slate-800/60">
              <CardTitle className="text-sm font-medium text-slate-300">Messages reçus</CardTitle>
              <MessageSquare className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-extrabold text-slate-100 tracking-tight">24</div>
              <p className="text-xs text-slate-400">
                +3 cette semaine
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 backdrop-blur supports-[backdrop-filter]:bg-slate-900/40 border border-slate-800/60 rounded-2xl shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_10px_35px_-15px_rgba(0,0,0,0.6)]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 border-b border-slate-800/60">
              <CardTitle className="text-sm font-medium text-slate-300">Temps de réponse</CardTitle>
              <MessageSquare className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-extrabold text-slate-100 tracking-tight">2h</div>
              <p className="text-xs text-slate-400">
                Temps moyen
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 backdrop-blur supports-[backdrop-filter]:bg-slate-900/40 border border-slate-800/60 rounded-2xl shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_10px_35px_-15px_rgba(0,0,0,0.6)]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 border-b border-slate-800/60">
              <CardTitle className="text-sm font-medium text-slate-300">Satisfaction</CardTitle>
              <MessageSquare className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-extrabold text-slate-100 tracking-tight">98%</div>
              <p className="text-xs text-slate-400">
                Étudiants satisfaits
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </InstructorLayout>
  );
};

export default InstructorMessagesPage;
